import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Badge, Alert, ProgressBar } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge, QueryState, EmptyState, KeyValue, StatCard, Money } from '@/components';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ReasonDialog from '../components/ReasonDialog';
import Trail from '../components/Trail';
import { useQueue } from '../hooks/useQueue';
import { filterRows, optionsWithCounts } from '../lib/filters';
import { availableActions } from '../lib/decisions';
import { formatDateTime, humanize } from '../lib/format';

const CHECK_ICON = { pass: CheckCircle2, fail: XCircle, warn: AlertTriangle };
const CHECK_CLASS = { pass: 'text-success-600', fail: 'text-error-600', warn: 'text-warning-600' };
const SEVERITY = { high: 'error', medium: 'warning', low: 'info' };
const OPEN = ['pending_review', 'flagged'];

/** ADMIN-02 Listing moderation across merchants (preview data). */
export default function Moderation() {
  const [params, setParams] = useSearchParams();
  const { query, rows, mutate } = useQueue('platformModeration');
  const [q, setQ] = useState('');
  const [merchant, setMerchant] = useState('all');
  const status = params.get('status') || 'open';
  const [action, setAction] = useState(null);
  const [msg, setMsg] = useState(null);

  const setParam = (key, v) => {
    const next = new URLSearchParams(params);
    next.set(key, v);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Listing moderation" subtitle="ADMIN-02 · inspection evidence, risk flags and publish decisions across merchants" />
      <QueryState query={query}>
        {() => {
          const open = rows.filter((r) => OPEN.includes(r.status));
          const scoped = status === 'open' ? open : filterRows(rows, { status });
          const list = filterRows(scoped, { merchant_slug: merchant }, q, ['title', 'unit_code', 'merchant', 'id']);
          const item = rows.find((r) => r.id === params.get('id')) || list[0] || null;
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="In queue" value={open.length} hint="pending or flagged" />
                <StatCard label="Flagged" value={rows.filter((r) => r.status === 'flagged').length} hint="need a manual decision" />
                <StatCard label="Price outliers" value={rows.filter((r) => r.flags.some((f) => f.code === 'PRICE_OUTLIER')).length} hint="±20% from median" />
                <StatCard label="Inspection fails" value={rows.filter((r) => r.flags.some((f) => f.code === 'INSPECTION_FAIL')).length} hint="block publishing" />
              </div>
              {msg ? <Alert variant="success" title={msg} dismissible onDismiss={() => setMsg(null)} /> : null}
              <div className="grid gap-4 xl:grid-cols-5">
                <SectionCard title="Queue" className="xl:col-span-2">
                  <FilterBar
                    compact
                    q={q}
                    onQ={setQ}
                    placeholder="Model, unit code, merchant…"
                    filters={[
                      { key: 'status', label: 'Queue', value: status, options: [{ value: 'open', label: `Open (${open.length})` }, ...optionsWithCounts(rows, 'status')] },
                      { key: 'merchant', label: 'Merchant', value: merchant, options: optionsWithCounts(rows, 'merchant_slug') },
                    ]}
                    onFilter={(k, v) => (k === 'merchant' ? setMerchant(v) : setParam('status', v))}
                  />
                  {list.length ? (
                    <DataTable
                      data={list}
                      onRowClick={(r) => setParam('id', r.id)}
                      rowClass={({ row }) => (item && row.id === item.id ? 'bg-primary-50' : '')}
                      columns={[
                        { key: 'model', label: 'Listing', render: (r) => (
                          <div>
                            <div className="font-medium">{r.model}</div>
                            {/* Type and condition (MERCH-89): every item that is not new is reviewed. */}
                            {r.product_type_label || r.condition_label ? <div className="text-xs opacity-70">{[r.product_type_label, r.condition_label].filter(Boolean).join(' · ')}</div> : null}
                            <div className="text-xs opacity-60">{r.merchant} · {r.price_max_paise > r.price_min_paise ? <><Money paise={r.price_min_paise} /> to <Money paise={r.price_max_paise} /></> : <Money paise={r.price_paise} />}</div>
                          </div>
                        ) },
                        { key: 'status', label: 'Status', render: (r) => (
                          <div className="space-y-1">
                            <StatusBadge status={r.status} />
                            {r.flags.length ? <div><Badge variant={r.flags.some((f) => f.severity === 'high') ? 'error' : 'warning'} size="xs">{r.flags.length} flag{r.flags.length > 1 ? 's' : ''}</Badge></div> : null}
                          </div>
                        ) },
                      ]}
                    />
                  ) : <EmptyState title="Queue is clear">No listings match this queue and search.</EmptyState>}
                </SectionCard>
                <div className="xl:col-span-3 space-y-4">
                  {item ? <ListingDetail item={item} onAction={setAction} /> : <EmptyState title="Select a listing" />}
                </div>
              </div>
              {item ? (
                <ReasonDialog
                  action={action}
                  subject={item.model}
                  onClose={() => setAction(null)}
                  reasonHint={item.flags.some((f) => f.severity === 'high') && action && action.key === 'approve' ? 'This listing has a high-severity flag: approving needs a written override.' : undefined}
                  onConfirm={async ({ reason }) => {
                    const row = await mutate('platformModerationDecide', { id: item.id, action: action.key, reason });
                    setMsg(`${item.id} ${humanize(row.status)}`);
                  }}
                />
              ) : null}
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

function ListingDetail({ item, onAction }) {
  const actions = availableActions('moderation', item);
  const failed = item.inspection.checks.filter((c) => c.result !== 'pass').length;
  return (
    <>
      <SectionCard
        title={<span className="flex flex-wrap items-center gap-2">{item.model} <StatusBadge status={item.status} /></span>}
        actions={<div className="flex flex-wrap gap-2">{actions.map((a) => <Button key={a.key} size="sm" variant={a.variant} onClick={() => onAction(a)}>{a.label}</Button>)}</div>}
      >
        <p className="text-sm opacity-80">{item.title}</p>
        {(item.review_reasons || []).map((r) => (
          <Alert key={r.code} variant="info" title={humanize(r.code)}>{r.message}</Alert>
        ))}
        {item.flags.map((f) => (
          <Alert key={f.code} variant={SEVERITY[f.severity]} title={`${humanize(f.code)} · ${f.severity}`}>{f.message}</Alert>
        ))}
        <KeyValue columns={2} items={[
          { label: 'Merchant', value: item.merchant },
          { label: 'Product type', value: item.product_type_label ? `${item.product_type_label}${item.industry_label ? ` (${item.industry_label})` : ''}` : '—' },
          { label: 'Condition', value: item.condition_label ? <Badge variant="outline">{item.condition_label}</Badge> : '—' },
          { label: 'Unit code', value: <code className="text-xs">{item.unit_code}</code> },
          { label: 'Serial', value: <code className="text-xs">{item.serial_masked}</code> },
          { label: 'Segment', value: item.segment },
          { label: 'Grade', value: <Badge variant="outline">{item.grade}</Badge> },
          { label: 'Warranty', value: `${item.warranty_months} months` },
          { label: 'Photos', value: `${item.photos} (exact unit)` },
          { label: 'Submitted', value: formatDateTime(item.submitted_at) },
        ]} />
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Price check">
          <KeyValue items={[
            { label: 'Listed price', value: <Money paise={item.price_paise} className="font-semibold" /> },
            { label: 'Market median', value: item.market_median_paise ? <Money paise={item.market_median_paise} /> : 'Not enough comparable listings yet' },
            item.price_delta_pct == null ? null : { label: 'Difference', value: <Badge variant={Math.abs(item.price_delta_pct) >= 20 ? 'error' : 'success'}>{item.price_delta_pct > 0 ? '+' : ''}{item.price_delta_pct}%</Badge> },
          ]} />
          {item.price_delta_pct == null ? <p className="text-sm opacity-70">No comparable listings across stores yet to check the price against.</p> : Math.abs(item.price_delta_pct) >= 20 ? (
            <p className="text-sm text-error-600">Outside the ±20% guardrail (risk policy v2). Check for a typo, a stolen unit, or a bait listing.</p>
          ) : <p className="text-sm opacity-70">Within the ±20% guardrail.</p>}
          {(item.prices || []).length > 1 ? (
            <div className="space-y-1">
              {/* Every price the listing sells at (its variants', MERCH-87): the approval covers all of them. */}
              <div className="text-sm font-medium">All {item.prices.length} prices, <Money paise={item.price_min_paise} /> to <Money paise={item.price_max_paise} /></div>
              <ul className="space-y-1 text-sm">
                {item.prices.map((x) => (
                  <li key={`${x.price_paise}-${x.mrp_paise}`} className="flex flex-wrap items-center gap-2">
                    <Money paise={x.price_paise} />
                    {x.mrp_paise != null ? <span className="opacity-60">MRP <Money paise={x.mrp_paise} /></span> : null}
                    {(x.flags || []).map((fl) => <Badge key={fl.code} variant={SEVERITY[fl.severity] || 'warning'} size="sm">{humanize(fl.code)}</Badge>)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </SectionCard>
        <SectionCard title="Battery">
          <div className="text-2xl font-bold">{item.battery_health_pct}%</div>
          <ProgressBar value={item.battery_health_pct} size="sm" ariaLabel="Battery health" />
          <p className="text-xs opacity-60">{item.battery_method}</p>
        </SectionCard>
      </div>

      <SectionCard title={`Inspection · ${item.inspection.score}/${item.inspection.max}`} actions={<Badge variant={failed ? 'warning' : 'success'} size="sm">{failed ? `${failed} issue${failed > 1 ? 's' : ''}` : 'All passed'}</Badge>}>
        <p className="text-xs opacity-60">By {item.inspection.technician} on {formatDateTime(item.inspection.date)}</p>
        <ul className="grid gap-2 sm:grid-cols-2">
          {item.inspection.checks.map((c) => {
            const Icon = CHECK_ICON[c.result];
            return (
              <li key={c.name} className="flex items-center gap-2 text-sm">
                <Icon size={16} className={CHECK_CLASS[c.result]} aria-hidden="true" />
                <span>{c.name}</span>
                <span className="sr-only">{c.result}</span>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <SectionCard title="Decision trail"><Trail entries={item.trail} /></SectionCard>
    </>
  );
}
